import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import { logAudit } from "@/lib/logAudit";
import { createNotification } from "@/lib/createNotification";
import Swal from "sweetalert2";

interface Client {
  id: string;
  tenant_id: string;
  full_name: string;
  email: string;
  phone: string;
}

interface Contract {
  id: string;
  title: string;
  total_amount: number;
  payment_status: string;
  status: string;
}

interface ClientAddon {
  id: string;
  addon_key: string;
  status: string;
  price: number;
  created_at: string;
}

interface DigitalInvitation {
  id: string;
  public_slug: string;
  status: string;
  is_published: boolean;
  template: string;
  created_at: string;
}

interface Props {
  client: Client;
  onClose: () => void;
}

const TABS = ["Overview", "Add-ons", "Invitations"] as const;
type Tab = (typeof TABS)[number];

function generateSlug(): string {
  return Math.random().toString(36).substring(2, 10) + Date.now().toString(36).substring(2, 6);
}

export default function AddOnsModal({ client, onClose }: Props) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("Overview");
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [contracts, setContracts] = useState<Contract[]>([]);
  const [clientAddon, setClientAddon] = useState<ClientAddon | null>(null);
  const [invitation, setInvitation] = useState<DigitalInvitation | null>(null);
  const [charges, setCharges] = useState<{ id: string; title: string; amount: number; status: string }[]>([]);

  const fetchData = useCallback(async () => {
    const tid = await getTenantId();
    setTenantId(tid);
    if (!tid) return;
    setLoading(true);

    const [contractsRes, addonRes, inviteRes, chargesRes] = await Promise.all([
      supabase
        .from("contracts")
        .select("id, title, total_amount, payment_status, status")
        .eq("tenant_id", tid)
        .eq("client_id", client.id)
        .order("created_at", { ascending: false }),
      supabase
        .from("client_addons")
        .select("id, addon_key, status, price, created_at")
        .eq("tenant_id", tid)
        .eq("client_id", client.id)
        .eq("addon_key", "digital_invitation")
        .maybeSingle(),
      supabase
        .from("digital_invitations")
        .select("id, public_slug, status, is_published, template, created_at")
        .eq("tenant_id", tid)
        .eq("client_id", client.id)
        .maybeSingle(),
      supabase
        .from("contract_charges")
        .select("id, title, amount, status")
        .eq("tenant_id", tid)
        .eq("client_id", client.id)
        .eq("title", "Digital Invitation Activation")
        .eq("status", "active"),
    ]);

    if (contractsRes.data) setContracts(contractsRes.data);
    if (addonRes.data) setClientAddon(addonRes.data);
    if (inviteRes.data) setInvitation(inviteRes.data);
    if (chargesRes.data) setCharges(chargesRes.data);
    setLoading(false);
  }, [client.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getInvitationStatus = (): "not_requested" | "requested" | "active" | "disabled" => {
    if (!clientAddon) return "not_requested";
    if (clientAddon.status === "disabled") return "disabled";
    if (clientAddon.status === "active" && invitation) return "active";
    if (clientAddon.status === "active" && !invitation) return "requested";
    return "not_requested";
  };

  const status = getInvitationStatus();

  const handleActivate = async () => {
    if (!tenantId) return;

    const activeContracts = contracts.filter((c) => c.status !== "cancelled" && c.status !== "void");

    if (activeContracts.length === 0) {
      const confirmResult = await Swal.fire({
        title: "No active contract",
        text: `Client "${client.full_name}" has no active contract. Activate invitation anyway?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Activate anyway",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#C8A96E",
        background: "#ffffff",
        color: "#0f172a",
      });
      if (!confirmResult.isConfirmed) return;
    }

    setLoading(true);

    // 1. Upsert client_addons
    const { error: addonErr } = await supabase
      .from("client_addons")
      .upsert(
        {
          tenant_id: tenantId,
          client_id: client.id,
          addon_key: "digital_invitation",
          status: "active",
          price: 50,
        },
        { onConflict: "tenant_id,client_id,addon_key" }
      );

    if (addonErr) {
      setLoading(false);
      await Swal.fire({
        title: "Error",
        text: addonErr.message,
        icon: "error",
        confirmButtonColor: "#C8A96E",
        background: "#ffffff",
        color: "#0f172a",
      });
      return;
    }

    // 2. Create digital_invitations if not exists
    let inviteId: string | null = invitation?.id ?? null;
    if (!invitation) {
      const { data: inviteData, error: inviteErr } = await supabase
        .from("digital_invitations")
        .insert({
          tenant_id: tenantId,
          client_id: client.id,
          public_slug: generateSlug(),
          status: "active",
          is_published: false,
          title: `${client.full_name}'s Event`,
          event_title: "Our Special Day",
          rsvp_enabled: false,
          welcome_message: "We are delighted to invite you to share this special day with us.",
          thank_you_message: "Thank you for celebrating with us!",
          theme_key: "classic",
        })
        .select("id")
        .maybeSingle();

      if (inviteErr) {
        setLoading(false);
        await Swal.fire({
          title: "Error",
          text: inviteErr.message,
          icon: "error",
          confirmButtonColor: "#C8A96E",
          background: "#ffffff",
          color: "#0f172a",
        });
        return;
      }
      if (inviteData) inviteId = inviteData.id;
    }

    // 3. Offer to add $50 charge to contract
    if (activeContracts.length > 0) {
      const contract = activeContracts[0];
      const existingCharge = charges.find((c) => c.status === "active");

      if (!existingCharge) {
        const chargeResult = await Swal.fire({
          title: "Add $50 charge?",
          text: `Add "Digital Invitation Activation" ($50) to contract "${contract.title || contract.id.slice(0, 8)}…"?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Yes, add charge",
          cancelButtonText: "Skip for now",
          confirmButtonColor: "#C8A96E",
          background: "#ffffff",
          color: "#0f172a",
        });

        if (chargeResult.isConfirmed) {
          const { error: chargeErr } = await supabase.from("contract_charges").insert({
            tenant_id: tenantId,
            contract_id: contract.id,
            client_id: client.id,
            title: "Digital Invitation Activation",
            amount: 50,
            status: "active",
          });

          if (chargeErr) {
            await Swal.fire({
              title: "Charge failed",
              text: chargeErr.message,
              icon: "error",
              confirmButtonColor: "#C8A96E",
              background: "#ffffff",
              color: "#0f172a",
            });
          } else {
            await createNotification({
              tenantId,
              type: "success",
              title: "Charge added",
              message: `Digital Invitation charge of $50 added to contract for ${client.full_name}.`,
            });
          }
        }
      }
    }

    await logAudit({
      tenantId,
      action: "addon_activated",
      entity: "digital_invitation",
      entityId: inviteId || undefined,
      entityName: client.full_name,
    });

    await createNotification({
      tenantId,
      type: "success",
      title: "Invitation activated",
      message: `Digital Invitation activated for ${client.full_name}.`,
    });

    setLoading(false);
    fetchData();

    await Swal.fire({
      title: "Activated!",
      text: "The digital invitation has been activated for this client.",
      icon: "success",
      confirmButtonColor: "#C8A96E",
      background: "#ffffff",
      color: "#0f172a",
      timer: 2000,
      timerProgressBar: true,
    });
  };

  const handleDisable = async () => {
    if (!tenantId) return;
    const confirm = await Swal.fire({
      title: "Disable invitation?",
      text: "The client will lose access to edit their invitation.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Disable",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      background: "#ffffff",
      color: "#0f172a",
    });
    if (!confirm.isConfirmed) return;

    setLoading(true);
    await supabase
      .from("client_addons")
      .update({ status: "disabled" })
      .eq("tenant_id", tenantId)
      .eq("client_id", client.id)
      .eq("addon_key", "digital_invitation");

    await supabase
      .from("digital_invitations")
      .update({ status: "disabled" })
      .eq("tenant_id", tenantId)
      .eq("client_id", client.id);

    await logAudit({
      tenantId,
      action: "addon_disabled",
      entity: "digital_invitation",
      entityId: invitation?.id || undefined,
      entityName: client.full_name,
    });

    setLoading(false);
    fetchData();
  };

  const handleReEnable = async () => {
    if (!tenantId) return;
    setLoading(true);
    await supabase
      .from("client_addons")
      .update({ status: "active" })
      .eq("tenant_id", tenantId)
      .eq("client_id", client.id)
      .eq("addon_key", "digital_invitation");

    await supabase
      .from("digital_invitations")
      .update({ status: "active" })
      .eq("tenant_id", tenantId)
      .eq("client_id", client.id);

    await logAudit({
      tenantId,
      action: "addon_reactivated",
      entity: "digital_invitation",
      entityId: invitation?.id || undefined,
      entityName: client.full_name,
    });

    setLoading(false);
    fetchData();
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-2xl mx-4 overflow-hidden shadow-xl flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#C8A96E]/10 text-[#9a7840] font-semibold text-sm shrink-0">
              {client.full_name.charAt(0)}
            </div>
            <div>
              <h2 className="text-[#0f172a] font-semibold text-base">{client.full_name}</h2>
              <p className="text-slate-400 text-xs">{client.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-6 py-2 border-b border-slate-100 shrink-0">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                tab === t ? "bg-[#C8A96E] text-white" : "text-slate-400 hover:text-slate-700"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {tab === "Overview" && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Email</p>
                  <p className="text-[#0f172a] text-sm font-medium">{client.email}</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Phone</p>
                  <p className="text-[#0f172a] text-sm font-medium">{client.phone || "—"}</p>
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <p className="text-slate-400 text-xs uppercase tracking-wider mb-3">Active Contracts</p>
                {contracts.filter((c) => c.status !== "cancelled" && c.status !== "void").length === 0 ? (
                  <p className="text-slate-400 text-sm">No active contracts for this client.</p>
                ) : (
                  <div className="space-y-2">
                    {contracts
                      .filter((c) => c.status !== "cancelled" && c.status !== "void")
                      .map((c) => (
                        <div
                          key={c.id}
                          className="flex items-center justify-between bg-white rounded-lg border border-slate-200 px-4 py-3"
                        >
                          <div>
                            <p className="text-[#0f172a] text-sm font-medium">{c.title || `Contract ${c.id.slice(0, 8)}…`}</p>
                            <p className="text-slate-400 text-xs">${c.total_amount?.toFixed(2) || "0.00"} · {c.payment_status}</p>
                          </div>
                          <button
                            onClick={() => navigate(`/admin/contracts/${c.id}`)}
                            className="text-[#9a7840] text-xs font-medium hover:underline cursor-pointer whitespace-nowrap"
                          >
                            View
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "Add-ons" && (
            <div className="space-y-6">
              {/* Digital Invitation Card */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100">
                      <i className="ri-mail-send-line text-amber-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-[#0f172a] text-sm font-semibold">Digital Invitation</p>
                      <p className="text-slate-400 text-xs">$50 activation · RSVP & share link</p>
                    </div>
                  </div>
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                      status === "active"
                        ? "bg-emerald-50 text-emerald-600"
                        : status === "requested"
                        ? "bg-amber-50 text-amber-600"
                        : status === "disabled"
                        ? "bg-red-50 text-red-600"
                        : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {status === "not_requested" ? "Not Requested" : status}
                  </span>
                </div>

                {status === "not_requested" && (
                  <div className="text-center py-6">
                    <p className="text-slate-400 text-sm mb-4">Client has not requested a digital invitation.</p>
                    <button
                      onClick={handleActivate}
                      disabled={loading}
                      className="px-5 py-2.5 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {loading ? "Activating…" : "Activate Invitation"}
                    </button>
                  </div>
                )}

                {status === "requested" && (
                  <div className="text-center py-6">
                    <p className="text-amber-600 text-sm mb-4">Invitation requested but not yet created.</p>
                    <button
                      onClick={handleActivate}
                      disabled={loading}
                      className="px-5 py-2.5 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {loading ? "Activating…" : "Activate Invitation"}
                    </button>
                  </div>
                )}

                {status === "active" && (
                  <div className="space-y-3">
                    {invitation && (
                      <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-[#0f172a] text-sm font-medium">Invitation Status</p>
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-600">
                            {invitation.is_published ? "Published" : "Draft"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-[#0f172a] text-sm font-medium">Template</p>
                          <p className="text-slate-500 text-sm capitalize">{invitation.template}</p>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-[#0f172a] text-sm font-medium">Public Link</p>
                          <p className="text-slate-500 text-sm font-mono truncate max-w-[200px]">
                            /invite/{invitation.public_slug}
                          </p>
                        </div>
                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={() => navigate(`/admin/invitations/${invitation.id}`)}
                            className="flex-1 px-4 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                          >
                            Edit in Admin
                          </button>
                          <button
                            onClick={() => navigate(`/invite/${invitation.public_slug}`)}
                            className="flex-1 px-4 py-2 border border-slate-200 text-slate-600 hover:text-[#0f172a] text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                          >
                            Preview
                          </button>
                        </div>
                      </div>
                    )}

                    {charges.length > 0 && (
                      <div className="bg-white rounded-lg border border-slate-200 p-4">
                        <p className="text-[#0f172a] text-sm font-medium mb-2">Contract Charges</p>
                        {charges.map((ch) => (
                          <div key={ch.id} className="flex items-center justify-between py-1">
                            <p className="text-slate-500 text-sm">{ch.title}</p>
                            <p className="text-[#0f172a] text-sm font-semibold">+${ch.amount.toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      onClick={handleDisable}
                      disabled={loading}
                      className="w-full px-4 py-2.5 border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Disable Invitation
                    </button>
                  </div>
                )}

                {status === "disabled" && (
                  <div className="text-center py-6">
                    <p className="text-red-600 text-sm mb-4">Invitation is currently disabled.</p>
                    <button
                      onClick={handleReEnable}
                      disabled={loading}
                      className="px-5 py-2.5 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {loading ? "Re-enabling…" : "Re-enable Invitation"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "Invitations" && (
            <div>
              {invitation ? (
                <div className="space-y-4">
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[#0f172a] text-sm font-semibold">Invitation Details</p>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          invitation.is_published ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"
                        }`}
                      >
                        {invitation.is_published ? "Published" : "Draft"}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-slate-400 text-xs">Slug</p>
                        <p className="text-[#0f172a] font-mono">{invitation.public_slug}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs">Template</p>
                        <p className="text-[#0f172a] capitalize">{invitation.theme_key || "classic"}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs">Status</p>
                        <p className="text-[#0f172a] capitalize">{invitation.status}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs">Created</p>
                        <p className="text-[#0f172a]">
                          {new Date(invitation.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => navigate(`/admin/invitations/${invitation.id}`)}
                      className="flex-1 px-5 py-2.5 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Open Editor
                    </button>
                    <button
                      onClick={() => navigate(`/invite/${invitation.public_slug}`)}
                      className="flex-1 px-5 py-2.5 border border-slate-200 text-slate-600 hover:text-[#0f172a] text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Preview Public
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#C8A96E]/10 mx-auto mb-3">
                    <i className="ri-mail-send-line text-[#9a7840] text-2xl"></i>
                  </div>
                  <p className="text-[#0f172a] font-medium text-sm mb-1">No invitation yet</p>
                  <p className="text-slate-400 text-xs mb-4">Activate the Digital Invitation add-on first.</p>
                  <button
                    onClick={() => setTab("Add-ons")}
                    className="px-4 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Go to Add-ons
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}